import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CellContext } from '@tanstack/react-table';

import { getEmbeddedTableRowRowSpan } from 'components/DataTable/helpers';
import { WebEDIRowData } from 'components/DataTable/Types';
import { Button } from 'components/Form/Button';

import { useDataTableContext } from '../../../contexts/DataTableContext';
import { generateEmbeddedActionTestId } from '../../../utils/generateEmbeddedActionTestId';

type DeleteEmbeddedRowCellProps = CellContext<WebEDIRowData, unknown>;

export const DeleteEmbeddedRowCell = ({
  row,
  column,
  table,
  rowWithEmbeddedTableRowInfo,
}: DeleteEmbeddedRowCellProps): JSX.Element => {
  const { t } = useTranslation();
  const { removeEmbedded } = useDataTableContext();
  const isSubmitting = table.options.meta?.isSubmitting ?? false;

  const parentColumnPath = column.parent?.id;
  const rowSpanInfo = getEmbeddedTableRowRowSpan(rowWithEmbeddedTableRowInfo, parentColumnPath);
  const itemIndex = rowSpanInfo!.itemIndex;

  const testId = useMemo(() => {
    return generateEmbeddedActionTestId({
      dataPrefix: table.options.meta?.dataPrefix,
      rowIndex: row.index,
      parentColumnPath,
      suffix: 'deleteEmbeddedRowCell',
      itemIndex,
    });
  }, [table.options.meta?.dataPrefix, parentColumnPath, row.index, itemIndex]);

  const handleDelete = useCallback(() => {
    removeEmbedded(row.index, itemIndex, parentColumnPath!);
  }, [removeEmbedded, row.index, parentColumnPath, itemIndex]);

  return (
    <div className="flex size-full items-center justify-center">
      <div className="flex justify-center">
        <Button
          iconOnly
          size="extra-small"
          variant="minimal"
          LeftIcon={XMarkIcon}
          onClick={handleDelete}
          analyticsId="table_embedded_item_remove"
          aria-label={t('webedi:deleteRow.remove')}
          testId={testId}
          disabled={isSubmitting}
        >
          {t('webedi:deleteRow.remove')}
        </Button>
      </div>
    </div>
  );
};
